// Self-playing Outlook compose window: the add-in injects the right signature
// for the selected persona (group template + language + campaign + survey +
// disclaimer) as the window opens, then an e-mail types itself and is sent.
// Everything is DOM — no screenshots — so it stays crisp and on-brand.
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from './primitives';
import { useT } from '../../../i18n';
import './ComposerDemo.css';

const messages = {
  personas: {
    tr: [
      {
        key: 'sales',
        name: 'Ayşe Yılmaz',
        initials: 'AY',
        title: 'Satış Müdürü',
        group: 'Satış',
        lang: 'TR',
        template: 'Satış · Türkçe',
        phone: '+90 212 555 01 24',
        email: 'ayse.yilmaz@nordalojistik.com.tr',
        to: 'elif.kaya@musteri.com.tr',
        subject: 'Teklifimiz hakkında',
        body: 'Merhaba Elif Hanım,\n\nGörüştüğümüz taşıma teklifinin detaylarını ekte paylaşıyorum. Sorularınız için her zaman buradayım.',
        signoff: 'Saygılarımla,',
        banner: { kicker: 'Kış Kampanyası', text: 'Tüm Avrupa rotalarında %15 indirim', cta: 'Teklif al →' },
        survey: 'Bu yazışmadan ne kadar memnun kaldınız?',
        legal: 'Bu e-posta ve ekleri yalnızca alıcısına özeldir. Yanlışlıkla aldıysanız lütfen göndericiye bildirip siliniz.',
      },
      {
        key: 'export',
        name: 'Can Öztürk',
        initials: 'CÖ',
        title: 'Export Manager',
        group: 'Dış Ticaret',
        lang: 'EN',
        template: 'Export · English',
        phone: '+90 212 555 01 61',
        email: 'can.ozturk@nordalojistik.com.tr',
        to: 'm.weber@partner-gmbh.de',
        subject: 'Shipment schedule for Q1',
        body: 'Hi Markus,\n\nPlease find the updated Q1 shipment schedule attached. Happy to walk you through it this week.',
        signoff: 'Best regards,',
        banner: { kicker: 'Meet us', text: 'Logistics Expo Istanbul · Hall 4, Stand B12', cta: 'Book a slot →' },
        survey: 'How satisfied are you with this conversation?',
        legal: 'This e-mail and any attachments are confidential and intended solely for the addressee.',
      },
      {
        key: 'hr',
        name: 'Zeynep Arslan',
        initials: 'ZA',
        title: 'İK İş Ortağı',
        group: 'İnsan Kaynakları',
        lang: 'TR',
        template: 'İK · Türkçe',
        phone: '+90 212 555 01 88',
        email: 'zeynep.arslan@nordalojistik.com.tr',
        to: 'aday.mert@gmail.com',
        subject: 'Mülakat davetiniz',
        body: 'Merhaba Mert Bey,\n\nBaşvurunuz için teşekkür ederiz. Sizi perşembe 14:00’te ofisimizde görüşmeye davet etmek isteriz.',
        signoff: 'İyi çalışmalar,',
        banner: { kicker: 'Kariyer', text: 'Norda’da 12 açık pozisyon seni bekliyor', cta: 'İncele →' },
        survey: null,
        legal: 'Kişisel verileriniz KVKK kapsamında yalnızca işe alım süreci için işlenmektedir.',
      },
    ],
    en: [
      {
        key: 'sales',
        name: 'Ayşe Yılmaz',
        initials: 'AY',
        title: 'Sales Manager',
        group: 'Sales',
        lang: 'EN',
        template: 'Sales · English',
        phone: '+90 212 555 01 24',
        email: 'ayse.yilmaz@nordalojistik.com.tr',
        to: 'emma.clarke@client.co.uk',
        subject: 'About our proposal',
        body: 'Hi Emma,\n\nI’ve attached the details of the freight proposal we discussed. Happy to answer any questions you may have.',
        signoff: 'Kind regards,',
        banner: { kicker: 'Winter Campaign', text: '15% off all European routes', cta: 'Get a quote →' },
        survey: 'How satisfied are you with this conversation?',
        legal: 'This e-mail and any attachments are confidential and intended solely for the addressee. If you received it in error, please notify the sender and delete it.',
      },
      {
        key: 'export',
        name: 'Can Öztürk',
        initials: 'CÖ',
        title: 'Exportleiter',
        group: 'Foreign Trade',
        lang: 'DE',
        template: 'Export · Deutsch',
        phone: '+90 212 555 01 61',
        email: 'can.ozturk@nordalojistik.com.tr',
        to: 'm.weber@partner-gmbh.de',
        subject: 'Versandplan für Q1',
        body: 'Guten Tag Herr Weber,\n\nanbei finden Sie den aktualisierten Versandplan für Q1. Gerne gehe ich ihn diese Woche mit Ihnen durch.',
        signoff: 'Mit freundlichen Grüßen,',
        banner: { kicker: 'Besuchen Sie uns', text: 'Logistics Expo Istanbul · Halle 4, Stand B12', cta: 'Termin buchen →' },
        survey: 'Wie zufrieden sind Sie mit dieser Korrespondenz?',
        legal: 'Diese E-Mail und etwaige Anhänge sind vertraulich und ausschließlich für den Empfänger bestimmt.',
      },
      {
        key: 'hr',
        name: 'Zeynep Arslan',
        initials: 'ZA',
        title: 'HR Business Partner',
        group: 'Human Resources',
        lang: 'EN',
        template: 'HR · English',
        phone: '+90 212 555 01 88',
        email: 'zeynep.arslan@nordalojistik.com.tr',
        to: 'mert.candidate@gmail.com',
        subject: 'Your interview invitation',
        body: 'Hi Mert,\n\nThank you for your application. We’d like to invite you to an interview at our office on Thursday at 2 pm.',
        signoff: 'Best wishes,',
        banner: { kicker: 'Careers', text: '12 open positions at Norda are waiting for you', cta: 'Explore →' },
        survey: null,
        legal: 'Your personal data is processed solely for the recruitment process, in line with applicable data protection law.',
      },
    ],
    },
  company: { tr: 'Norda Lojistik', en: 'Norda Logistics' },
  personasAria: { tr: 'Örnek çalışanlar', en: 'Sample employees' },
  play: { tr: 'Oynat', en: 'Play' },
  groupLine: { tr: '{group} grubu · {lang}', en: '{group} group · {lang}' },
  newMessage: { tr: 'Yeni ileti', en: 'New message' },
  adding: { tr: 'Martinci · imza ekleniyor', en: 'Martinci · adding signature' },
  added: { tr: 'Martinci · imza eklendi', en: 'Martinci · signature added' },
  opening: { tr: 'Yeni ileti açılıyor…', en: 'Opening new message…' },
  to: { tr: 'Kime', en: 'To' },
  subject: { tr: 'Konu', en: 'Subject' },
  tagDirectory: { tr: 'Entra ID’den otomatik', en: 'Auto-filled from Entra ID' },
  tagCampaign: { tr: 'Kampanya · takvimli', en: 'Campaign · scheduled' },
  tagSurvey: { tr: 'Memnuniyet anketi', en: 'Satisfaction survey' },
  tagLegal: { tr: 'Yasal uyarı · gruba özel', en: 'Disclaimer · group-specific' },
  send: { tr: 'Gönder', en: 'Send' },
  footerNote: { tr: 'Kullanıcı hiçbir şey seçmedi · imza ileti açılınca geldi', en: 'The user chose nothing · the signature arrived as the message opened' },
  sent: {
    tr: ({ template }) => <>Gönderildi — <strong>{template}</strong> şablonuyla</>,
    en: ({ template }) => <>Sent — with the <strong>{template}</strong> template</>,
  },
};

const EMPTY = { to: '', subject: '', body: '', phase: 'opening' };

function NordaMark() {
  return (
    <svg viewBox="0 0 40 40" className="lp-cd-logo" aria-hidden="true">
      <rect width="40" height="40" rx="10" />
      <path d="M11 29V11l18 18V11" />
    </svg>
  );
}

export function ComposerDemo() {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: '-20% 0px -20% 0px' });
  const reduce = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const [s, setS] = useState(EMPTY);
  const { t } = useT(messages);
  const personas = t('personas');
  const persona = personas[idx];

  useEffect(() => {
    if (!inView) return undefined;
    let cancelled = false;
    const timers = [];
    const wait = (ms) => new Promise((r) => { timers.push(setTimeout(r, ms)); });
    const type = async (field, text, speed) => {
      for (let i = 1; i <= text.length; i += 1) {
        if (cancelled) return;
        const v = text.slice(0, i);
        setS((prev) => ({ ...prev, [field]: v }));
        // eslint-disable-next-line no-await-in-loop
        await wait(speed + (text[i - 1] === ' ' ? speed : 0));
      }
    };

    (async () => {
      if (reduce) {
        setS({ to: persona.to, subject: persona.subject, body: persona.body, phase: 'sent' });
        return;
      }
      // Mirrors the add-in: OnNewMessageCompose inserts the signature the
      // moment the compose window opens — before the user types anything.
      setS(EMPTY);
      await wait(450);
      if (cancelled) return;
      setS((p) => ({ ...p, phase: 'inject' }));
      await wait(800);
      if (cancelled) return;
      setS((p) => ({ ...p, phase: 'typing' }));
      await wait(1500);
      await type('to', persona.to, 26);
      await wait(250);
      await type('subject', persona.subject, 34);
      await wait(250);
      await type('body', persona.body, 11);
      if (cancelled) return;
      await wait(600);
      setS((p) => ({ ...p, phase: 'press' }));
      await wait(500);
      if (cancelled) return;
      setS((p) => ({ ...p, phase: 'sent' }));
      await wait(3200);
      if (cancelled) return;
      setIdx((i) => (i + 1) % personas.length);
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [idx, inView, reduce, persona, personas.length]);

  const signed = s.phase !== 'opening' && s.phase !== 'inject';
  const typingField = s.phase === 'typing'
    ? (s.body ? 'body' : s.subject ? 'subject' : 'to')
    : null;

  return (
    <div className="lp-cd" ref={ref}>
      <div className="lp-cd-personas" role="tablist" aria-label={t('personasAria')}>
        {personas.map((p, i) => (
          <button
            key={p.key}
            role="tab"
            aria-selected={i === idx}
            className={`lp-cd-persona ${i === idx ? 'is-active' : ''}`}
            onClick={() => setIdx(i)}
            data-cursor={t('play')}
          >
            <span className="lp-cd-avatar">{p.initials}</span>
            <span className="lp-cd-persona-text">
              <strong>{p.name}</strong>
              <small>{t('groupLine', { group: p.group, lang: p.lang })}</small>
            </span>
            <span className="lp-cd-persona-rule">→ {p.template}</span>
            {i === idx && <motion.span layoutId="lp-cd-progress" className="lp-cd-persona-bar" />}
          </button>
        ))}
      </div>

      <motion.div
        key={`${persona.key}-${persona.lang}`}
        className="lp-cd-window"
        initial={reduce ? false : { opacity: 0, y: 28, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: EASE_OUT }}
      >
        <div className="lp-cd-chrome">
          <div className="lp-cd-chrome-left">
            <span className="lp-cd-chrome-title">{t('newMessage')}</span>
            <span className="lp-cd-chrome-from">{persona.email}</span>
          </div>
          <span className={`lp-cd-addin ${s.phase === 'inject' ? 'is-busy' : ''} ${signed ? 'is-done' : ''}`}>
            <span className="lp-cd-addin-dot" />
            {s.phase === 'inject' ? t('adding') : signed ? t('added') : t('opening')}
          </span>
        </div>

        <div className="lp-cd-field">
          <span>{t('to')}</span>
          <p>{s.to}{typingField === 'to' && <i className="lp-cd-caret" />}</p>
        </div>
        <div className="lp-cd-field">
          <span>{t('subject')}</span>
          <p>{s.subject}{typingField === 'subject' && <i className="lp-cd-caret" />}</p>
        </div>

        <div className="lp-cd-body">
          <p className="lp-cd-text">{s.body}{typingField === 'body' && <i className="lp-cd-caret" />}</p>

          <AnimatePresence mode="wait">
            {signed && (
              <motion.div
                key={persona.key}
                className="lp-cd-sig"
                initial="hidden"
                animate="show"
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                variants={{ show: { transition: { staggerChildren: 0.16 } } }}
              >
                <SigPart className="lp-cd-signoff">{persona.signoff}</SigPart>
                <SigPart className="lp-cd-card" tag={t('tagDirectory')}>
                  <NordaMark />
                  <div>
                    <strong>{persona.name}</strong>
                    <span>{persona.title} · {t('company')}</span>
                    <small>{persona.phone} · nordalojistik.com.tr</small>
                  </div>
                </SigPart>
                <SigPart className="lp-cd-banner" tag={t('tagCampaign')}>
                  <span className="lp-cd-banner-kicker">{persona.banner.kicker}</span>
                  <span className="lp-cd-banner-text">{persona.banner.text}</span>
                  <span className="lp-cd-banner-cta">{persona.banner.cta}</span>
                </SigPart>
                {persona.survey && (
                  <SigPart className="lp-cd-survey" tag={t('tagSurvey')}>
                    <span>{persona.survey}</span>
                    <span className="lp-cd-stars" aria-hidden="true">★★★★★</span>
                  </SigPart>
                )}
                <SigPart className="lp-cd-legal" tag={t('tagLegal')}>{persona.legal}</SigPart>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="lp-cd-footer">
          <span className={`lp-cd-send ${s.phase === 'press' ? 'is-press' : ''}`}>
            {t('send')}
          </span>
          <span className="lp-cd-footer-note">{t('footerNote')}</span>
        </div>

        <AnimatePresence>
          {s.phase === 'sent' && (
            <motion.div
              className="lp-cd-toast"
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.5, ease: EASE_OUT }}
            >
              <span className="lp-tick lp-cd-toast-check" aria-hidden="true" />
              {t('sent', { template: persona.template })}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function SigPart({ children, className, tag }) {
  return (
    <motion.div
      className="lp-cd-part"
      variants={{
        hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
        show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: EASE_OUT } },
      }}
    >
      <div className={className}>{children}</div>
      {tag && <span className="lp-cd-tag">{tag}</span>}
    </motion.div>
  );
}

export default ComposerDemo;
